import { useAuth0 } from "@auth0/auth0-react";
import { jwtDecode } from "jwt-decode";
import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * ClaimMosaic JWT Token Claims
 * Custom claims added to Auth0 JWT token
 */
export interface ClaimMosaicTokenClaims {
  "https://claimmosaic.com/customerId": string;
  "https://claimmosaic.com/contactId": string;
  "https://claimmosaic.com/userId": number;
  "https://claimmosaic.com/primaryTenant": string;
  "https://claimmosaic.com/name": string;
  "https://claimmosaic.com/role": string;
  "https://claimmosaic.com/accountTier": string;
  "https://claimmosaic.com/subscriptionStatus": string;
  "https://claimmosaic.com/isLockout": boolean;
  "https://claimmosaic.com/loginSource": string;
  "https://claimmosaic.com/permissions": string[];
  "https://claimmosaic.com/email": string;
  "https://claimmosaic.com/defaultEmail": string;
  iss: string;
  sub: string;
  aud: string[] | string;
  iat: number;
  exp: number;
  scope?: string;
  azp?: string;
}

export interface AuthClaimsState {
  claims: ClaimMosaicTokenClaims | null;
  permissions: string[];
  loading: boolean;
  error: string | null;
  hasPermission: (permission: string) => boolean;
}

function normalizePermissions(namespacedPermissions?: string[]): string[] {
  return Array.from(
    new Set(
      (namespacedPermissions ?? [])
        .map((value) => value?.trim().toLowerCase())
        .filter((value): value is string => Boolean(value))
    )
  );
}

/**
 * Centralized claims + permission source for Auth0.
 * Fetches and decodes the JWT once per auth state refresh.
 */
export function useAuthClaims(): AuthClaimsState {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [claims, setClaims] = useState<ClaimMosaicTokenClaims | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;

    if (!isAuthenticated) {
      setClaims(null);
      setPermissions([]);
      setLoading(false);
      setError(null);
      return;
    }

    const loadClaims = async () => {
      setLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const decoded = jwtDecode<ClaimMosaicTokenClaims>(token);
        const nextPermissions = normalizePermissions(
          decoded["https://claimmosaic.com/permissions"]
        );

        if (!isCancelled) {
          setClaims(decoded);
          setPermissions(nextPermissions);
          setError(null);
        }
      } catch (claimsError) {
        if (!isCancelled) {
          setClaims(null);
          setPermissions([]);
          setError(
            claimsError instanceof Error
              ? claimsError.message
              : "Failed to read Auth0 claims."
          );
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    void loadClaims();

    return () => {
      isCancelled = true;
    };
  }, [getAccessTokenSilently, isAuthenticated]);

  const hasPermission = useCallback(
    (permission: string) => permissions.includes(permission.toLowerCase()),
    [permissions]
  );

  return useMemo(
    () => ({
      claims,
      permissions,
      loading,
      error,
      hasPermission
    }),
    [claims, permissions, loading, error, hasPermission]
  );
}

/**
 * Backward compatible helper for existing callsites.
 */
export function useAuth0Permissions(permission: string): boolean {
  return useAuthClaims().hasPermission(permission);
}

/**
 * Backward compatible helper for existing callsites.
 */
export function useAuth0AllPermissions(): string[] {
  return useAuthClaims().permissions;
}
